import React, { forwardRef } from 'react';
import { Box, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import type { SelectChangeEvent, SelectProps } from '@mui/material';

import { Neutrals } from '../../color-palette/colors';

import type { FieldBaseProps, SelectOption } from './field.types';
import { FieldShell, describedBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { controlTextSx, placeholderSx } from './fieldStyles';

export interface SelectInputProps<T extends string | number> extends FieldBaseProps {
  options: readonly SelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T | null, event: SelectChangeEvent<T | ''>) => void;
  placeholder?: React.ReactNode;
  emptyOption?: React.ReactNode;
  name?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  renderValue?: (option: SelectOption<T>) => React.ReactNode;
  renderOption?: (option: SelectOption<T>) => React.ReactNode;
  MenuProps?: SelectProps['MenuProps'];
}

const optionSx = { minWidth: 0 } as const;
const optionLabelSx = { display: 'block' } as const;
const optionDescriptionSx = { color: Neutrals[600] } as const;

export const OptionContent = <T extends string | number>({ option }: { option: SelectOption<T> }) => (
  <Box sx={optionSx}>
    <Typography component="span" sx={optionLabelSx}>
      {option.label}
    </Typography>
    {option.description && (
      <Typography component="span" variant="caption2" sx={optionDescriptionSx}>
        {option.description}
      </Typography>
    )}
  </Box>
);

const renderDefaultOption = <T extends string | number>(option: SelectOption<T>) => <OptionContent option={option} />;

function SelectInputInner<T extends string | number>(props: SelectInputProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    id: idProp, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx,
    options, value, onChange, placeholder, emptyOption, name, autoFocus, readOnly, renderValue, renderOption = renderDefaultOption, MenuProps,
  } = props;
  const ids = useFieldIds(idProp);
  const current = value ?? '';
  const selected = options.find((o) => o.value === current);

  return (
    <FieldShell ref={ref} ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={size} fullWidth={fullWidth} className={className} sx={sx}>
      <Select<T | ''>
        id={ids.id}
        labelId={ids.labelId}
        name={name}
        autoFocus={autoFocus}
        readOnly={readOnly}
        displayEmpty
        value={current}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(raw === '' ? null : (raw as T), event);
        }}
        input={<OutlinedInput notched={false} sx={controlTextSx(size)} />}
        SelectDisplayProps={{ 'aria-describedby': describedBy(ids, helperText, describedById), 'aria-invalid': error || undefined }}
        MenuProps={MenuProps}
        renderValue={(raw) => {
          if (raw === '' || !selected) return <Box component="span" sx={placeholderSx}>{placeholder ?? emptyOption ?? ''}</Box>;
          return renderValue ? renderValue(selected) : selected.label;
        }}
      >
        {emptyOption !== undefined && <MenuItem value="">{emptyOption}</MenuItem>}
        {options.map((o) => (
          <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled}>
            {renderOption(o)}
          </MenuItem>
        ))}
      </Select>
    </FieldShell>
  );
}

export const SelectInput = forwardRef(SelectInputInner) as <T extends string | number>(
  props: SelectInputProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
